import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useFeatureFlag } from '../../contexts/FeatureFlagContext';
import { aiAgentsAPI, aiAPI } from '../../services/api';
import toast from '../../services/toast';
import {
  formatNavigationScopeLabel,
  navigationContextFromPick,
  navigationContextFromRoute,
} from '../../utils/aiNavigationContext';
import {
  pushRecentEntity,
  recordRecentFromRoute,
} from '../../utils/aiRecentEntities';
import {
  createScopeChangeMessage,
  shouldInsertScopeChange,
} from './aiChatScopeChange';
import {
  buildChatExportFilename,
  copyTextToClipboard,
  downloadChatPdf,
  downloadTextFile,
  formatChatMarkdown,
  formatChatPlainText,
  getExportableMessages,
  stripPlaceholders,
} from './chatExport';
import { shouldInsertIndicator } from './chatIndicatorHelpers';
import { useAIChatSend } from './useAIChatSend';
import { useAIChatThreads } from './useAIChatThreads';
import { useSlashCommands } from './useSlashCommands';

export interface UseVantiChatOptions {
  /** Load agents/threads on mount (true for full-page, false for panel which loads on open). */
  autoLoad?: boolean;
  /** Called after navigating to a path (e.g. panel can close itself). */
  onAfterNavigate?: () => void;
}

export function useVantiChat({ autoLoad = false, onAfterNavigate }: UseVantiChatOptions = {}) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<any[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [configured, setConfigured] = useState<any>(null);
  const [disabledByOrg, setDisabledByOrg] = useState(false);
  const [agents, setAgents] = useState<any[]>([]);
  const [selectedAgentId, setSelectedAgentId] = useState(
    () => localStorage.getItem('vanti.selectedAgentId') || '',
  );
  const [cooldownUntil, setCooldownUntil] = useState<any>(null);
  const [, forceTickUpdate] = useState(0);
  const [lastSendPayload, setLastSendPayload] = useState<any>(null);
  const [feedbackState, setFeedbackState] = useState<any>({});
  const [memoryPanelOpen, setMemoryPanelOpen] = useState(false);
  const [streamingMessageId, setStreamingMessageId] = useState<any>(null);
  const [streamingStatus, setStreamingStatus] = useState('');
  const [copiedResponseId, setCopiedResponseId] = useState<any>(null);
  const [chatMode, setChatMode] = useState(
    () => localStorage.getItem('vanti.chatMode') || 'auto',
  );
  const [actionMenuOpen, setActionMenuOpen] = useState(false);
  const [scopeChip, setScopeChip] = useState('');
  const [scopeOverride, setScopeOverride] = useState<any>(null);
  const [indexHint, setIndexHint] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [queuedQuestion, setQueuedQuestion] = useState<string>('');

  const actionMenuRef = useRef<any>(null);
  const abortControllerRef = useRef<any>(null);
  const sendRef = useRef<any>(null);
  const retryLastRef = useRef<any>(null);
  const inputRef = useRef<any>(null);
  const listRef = useRef<any>(null);

  const { pathname } = useLocation();
  const params = useParams();
  const navigationContextEnabled = useFeatureFlag('api.ai.navigationContext', true);
  const ragContextEnabled = useFeatureFlag('api.ai.ragContext', true);

  const {
    threadId,
    setThreadId,
    threadsOpen,
    setThreadsOpen,
    threads,
    setThreads,
    threadsLoading,
    currentThreadTitle,
    setCurrentThreadTitle,
    loadThreads,
    activeThreadDisplayTitle,
    handleSelectThread,
    handleDeleteThread,
    clearThreadState,
  } = useAIChatThreads({
    open: autoLoad ? true : undefined,
    alwaysLoad: autoLoad,
    selectedAgentId,
    agents,
    setSelectedAgentId,
    setMessages,
    setFeedbackState,
    messages,
  });

  const routeNavigationContext = useMemo(() => {
    if (!navigationContextEnabled) return null;
    return navigationContextFromRoute(pathname, params);
  }, [pathname, params, navigationContextEnabled]);

  const effectiveNavigationContext = scopeOverride || routeNavigationContext;

  // --- Cooldown tick ---
  const cooldownRemainingMs = cooldownUntil ? Math.max(0, cooldownUntil - Date.now()) : 0;
  const onCooldown = cooldownRemainingMs > 0;
  useEffect(() => {
    if (!onCooldown) return;
    const t = setInterval(() => forceTickUpdate((n) => n + 1), 250);
    return () => clearInterval(t);
  }, [onCooldown]);

  // --- Question queue: drain when loading finishes ---
  useEffect(() => {
    if (loading || !queuedQuestion) return;
    const queued = queuedQuestion;
    setQueuedQuestion('');
    sendRef.current?.(queued);
  }, [loading, queuedQuestion]);

  // --- AI status check ---
  useEffect(() => {
    aiAPI
      .status()
      .then((r) => {
        setDisabledByOrg(false);
        setConfigured(r.configured ?? false);
      })
      .catch((e) => {
        if (e && e.code === 'FEATURE_DISABLED') {
          setDisabledByOrg(true);
          setConfigured(false);
          return;
        }
        setDisabledByOrg(false);
        setConfigured(false);
      });
  }, []);

  // --- Load agents (immediately if autoLoad, otherwise guarded by caller) ---
  const loadAgents = useCallback(() => {
    aiAgentsAPI
      .listAgents({ scope: 'all' })
      .then((r) => setAgents(r.agents || []))
      .catch(() => setAgents([]));
  }, []);

  const loadDefaults = useCallback(() => {
    if (localStorage.getItem('vanti.selectedAgentId')) return;
    aiAgentsAPI
      .getDefaults()
      .then((r) => {
        const nextId = r.userDefaultAgentId || r.orgDefaultAgentId || '';
        if (nextId) {
          setSelectedAgentId(nextId);
          localStorage.setItem('vanti.selectedAgentId', nextId);
        }
      })
      .catch(() => null);
  }, []);

  useEffect(() => {
    if (!autoLoad) return;
    loadAgents();
    loadDefaults();
  }, [autoLoad, loadAgents, loadDefaults]);

  // --- Scope chip sync ---
  useEffect(() => {
    if (!navigationContextEnabled) {
      setScopeChip('');
      return;
    }
    setScopeChip(formatNavigationScopeLabel(scopeOverride || routeNavigationContext));
    recordRecentFromRoute(pathname, params, scopeOverride || routeNavigationContext);
  }, [routeNavigationContext, scopeOverride, navigationContextEnabled, pathname, params]);

  // --- Action menu outside-click ---
  useEffect(() => {
    if (!actionMenuOpen) return;
    const handler = (e) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target)) {
        setActionMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [actionMenuOpen]);

  // --- vanti:ask global event ---
  useEffect(() => {
    const handler = (e: any) => {
      const question = e.detail?.question;
      if (question) setInput(String(question));
    };
    window.addEventListener('vanti:ask', handler);
    return () => window.removeEventListener('vanti:ask', handler);
  }, []);

  // --- Mode ---
  const handleModeChange = (mode) => {
    const prev = chatMode;
    setChatMode(mode);
    localStorage.setItem('vanti.chatMode', mode);
    if (prev !== mode && messages.length > 0) {
      const modeLabels: Record<string, string> = {
        auto: 'Auto',
        quick: 'Quick Answer',
        interactive: 'Guided',
        analyze: 'Analyze',
        code: 'Code',
        report: 'Report',
      };
      const modeLabel = modeLabels[mode] || mode;
      setMessages((prev) => {
        if (!shouldInsertIndicator(prev, 'mode_change', 'modeLabel', modeLabel)) return prev;
        return [...prev, { id: `mode-${Date.now()}`, type: 'mode_change', modeId: mode, modeLabel }];
      });
    }
  };

  // --- Agent ---
  const handleAgentChange = (e) => {
    const nextId = e.target.value;
    const prevId = selectedAgentId;
    setSelectedAgentId(nextId);
    localStorage.setItem('vanti.selectedAgentId', nextId);
    if (prevId !== nextId && messages.length > 0) {
      const agentLabel = nextId
        ? agents.find((a) => a.id === nextId)?.name || 'Custom Agent'
        : 'Vanti (built-in)';
      setMessages((prevMsgs) => {
        if (!shouldInsertIndicator(prevMsgs, 'agent_change', 'agentLabel', agentLabel))
          return prevMsgs;
        return [...prevMsgs, { id: `agent-${Date.now()}`, type: 'agent_change', agentLabel }];
      });
    }
  };

  const handleAgentSelect = (agentId: string) => {
    handleAgentChange({ target: { value: agentId } });
  };

  // --- Scope ---
  const pickScope = async (pick) => {
    const ctx = navigationContextFromPick(pick);
    if (!ctx) return;
    setScopeOverride(ctx);
    setScopeChip(formatNavigationScopeLabel(pick));
    pushRecentEntity({ kind: pick.kind, id: pick.id, label: pick.label });
    const label = pick.label || formatNavigationScopeLabel(pick);
    setMessages((prev) => {
      if (prev.length === 0 || !label || !shouldInsertScopeChange(prev, label)) return prev;
      return [...prev, createScopeChangeMessage({ kind: pick.kind, label })];
    });
  };

  // --- Send / Stop / Retry ---
  const { send, retryLast, handleStop } = useAIChatSend({
    input,
    setInput,
    messages,
    setMessages,
    loading,
    setLoading,
    configured,
    disabledByOrg,
    setDisabledByOrg,
    chatMode,
    selectedAgentId,
    threadId,
    setThreadId,
    threads,
    effectiveNavigationContext,
    scopeChip,
    setScopeChip,
    setScopeOverride,
    setIndexHint,
    currentThreadTitle,
    setCurrentThreadTitle,
    setThreads,
    loadThreads,
    setCooldownUntil,
    lastSendPayload,
    setLastSendPayload,
    setStreamingMessageId,
    setStreamingStatus,
    onCooldown,
    abortControllerRef,
  });

  sendRef.current = send;
  retryLastRef.current = retryLast;

  // --- Feedback ---
  const handleFeedback = async (messageId, turnId, value) => {
    if (!turnId || feedbackState[messageId]) return;
    try {
      await aiAgentsAPI.submitFeedback({
        rating: value,
        threadId: threadId || undefined,
        messageId: turnId,
        agentId: selectedAgentId || undefined,
      });
      setFeedbackState((prev) => ({ ...prev, [messageId]: value }));
    } catch (err) {
      toast.error((err as Error).message || 'Failed to submit feedback');
    }
  };

  // --- Export ---
  const exportMeta = useMemo(() => {
    const agent = agents.find((a) => a.id === selectedAgentId);
    return {
      threadTitle: activeThreadDisplayTitle || undefined,
      threadId: threadId || undefined,
      agentName: agent?.name || undefined,
    };
  }, [agents, selectedAgentId, activeThreadDisplayTitle, threadId]);

  const hasExportableMessages = useMemo(
    () => getExportableMessages(messages).length > 0,
    [messages],
  );

  const handleCopyConversation = useCallback(async () => {
    const text = formatChatPlainText(messages, exportMeta);
    const ok = await copyTextToClipboard(text, { successMessage: 'Conversation copied' });
    if (ok) setActionMenuOpen(false);
  }, [messages, exportMeta]);

  const handleExportMarkdown = useCallback(() => {
    const text = formatChatMarkdown(messages, exportMeta);
    if (!text.trim()) { toast.error('Nothing to export'); return; }
    downloadTextFile(text, buildChatExportFilename(exportMeta, 'md'), 'text/markdown;charset=utf-8');
    toast.success('Chat exported');
    setActionMenuOpen(false);
  }, [messages, exportMeta]);

  const handleExportPdf = useCallback(() => {
    const text = formatChatPlainText(messages, exportMeta);
    if (!text.trim()) { toast.error('Nothing to export'); return; }
    const title = exportMeta.threadTitle ? `Vanti — ${exportMeta.threadTitle}` : 'Vanti — Conversation';
    downloadChatPdf(text, buildChatExportFilename(exportMeta, 'pdf'), title);
    toast.success('Chat exported');
    setActionMenuOpen(false);
  }, [messages, exportMeta]);

  const handleCopyResponse = useCallback(async (messageId, content) => {
    const ok = await copyTextToClipboard(stripPlaceholders(content), { successMessage: 'Response copied' });
    if (ok) {
      setCopiedResponseId(messageId);
      setTimeout(() => setCopiedResponseId((cur) => (cur === messageId ? null : cur)), 2000);
    }
  }, []);

  // --- Chat controls ---
  const clearChat = useCallback(() => {
    handleStop();
    setMessages([]);
    clearThreadState();
    setScopeOverride(null);
    setIndexHint('');
    setFeedbackState({});
    setStreamingMessageId(null);
    setScopeChip(formatNavigationScopeLabel(routeNavigationContext));
  }, [handleStop, clearThreadState, routeNavigationContext]);

  const sendOrQueue = useCallback((text?: string) => {
    const q = String(text ?? input).trim();
    if (!q) return;
    if (loading) {
      setQueuedQuestion(q);
      setInput('');
    } else {
      send(text);
    }
  }, [loading, input, send]);

  // --- Slash commands ---
  const slashCommands = useSlashCommands({
    input,
    setInput,
    sendOrQueue,
    clearChat,
    setThreadsOpen,
  });

  // Wrap send: apply pending format modifier to message text
  const sendOrQueueWithFormat = useCallback((text?: string) => {
    const { formatModifier, clearFormatModifier } = slashCommands;
    if (formatModifier && text) {
      clearFormatModifier();
      sendOrQueue(`${text} ${formatModifier.suffix}`);
    } else if (formatModifier && !text && input.trim()) {
      clearFormatModifier();
      sendOrQueue(`${input.trim()} ${formatModifier.suffix}`);
    } else {
      sendOrQueue(text);
    }
  }, [slashCommands, sendOrQueue, input]);

  const handleKeyDown = (e) => {
    // Let slash command picker consume the event first
    if (slashCommands.handleKeyDown(e as any)) return;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendOrQueueWithFormat();
    }
  };

  // --- Message callbacks ---
  const handleMessageAnswer = useCallback((msgId, qi, answer) => {
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === msgId
          ? { ...msg, questions: msg.questions.map((mq, mqi) => (mqi === qi ? { ...mq, _answered: true } : mq)) }
          : msg,
      ),
    );
    sendRef.current?.(answer);
  }, []);

  const handleMessageScopeSelect = useCallback((msgId, pick, originalQuestion) => {
    const ctx = navigationContextFromPick(pick);
    if (!ctx) return;
    setScopeOverride(ctx);
    setScopeChip(formatNavigationScopeLabel(pick));
    pushRecentEntity({ kind: pick.kind, id: pick.id, label: pick.label || pick.name });
    const label = pick.label || pick.name || formatNavigationScopeLabel(pick);
    setMessages((prev) => {
      let next = prev.map((msg) => (msg.id === msgId ? { ...msg, scopeResolved: true } : msg));
      if (label && shouldInsertScopeChange(next, label)) {
        next = [...next, createScopeChangeMessage({ kind: pick.kind, label })];
      }
      return next;
    });
    if (originalQuestion) sendRef.current?.(originalQuestion, ctx);
  }, []);

  const handleMessageInlineScopeSelect = useCallback((msgId, item, originalQuestion) => {
    const ctx = navigationContextFromPick(item);
    if (!ctx) return;
    setScopeOverride(ctx);
    setScopeChip(formatNavigationScopeLabel(item));
    pushRecentEntity({ kind: item.kind, id: item.id, label: item.label || item.name });
    const label = item.label || item.name || formatNavigationScopeLabel(item);
    setMessages((prev) => {
      let next = prev.map((msg) => (msg.id === msgId ? { ...msg, scopeResolved: true } : msg));
      if (label && shouldInsertScopeChange(next, label)) {
        next = [...next, createScopeChangeMessage({ kind: item.kind, label })];
      }
      return next;
    });
    if (originalQuestion) sendRef.current?.(originalQuestion, ctx);
  }, []);

  const handleMessageNavigate = useCallback(
    (path) => {
      navigate(path);
      onAfterNavigate?.();
    },
    [navigate, onAfterNavigate],
  );

  const handleSetInputFromAction = useCallback((text) => {
    setInput(text);
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const handleFollowUp = useCallback((q) => {
    setInput(q);
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const handleRetryFailed = useCallback((indicatorId, payload) => {
    setMessages((prev) => prev.filter((m) => m.id !== indicatorId));
    setLastSendPayload(payload);
    retryLastRef.current?.(payload);
  }, []);

  return {
    // State
    messages,
    setMessages,
    input,
    setInput,
    loading,
    configured,
    disabledByOrg,
    agents,
    selectedAgentId,
    cooldownRemainingMs,
    onCooldown,
    feedbackState,
    memoryPanelOpen,
    setMemoryPanelOpen,
    streamingMessageId,
    streamingStatus,
    copiedResponseId,
    chatMode,
    actionMenuOpen,
    setActionMenuOpen,
    scopeChip,
    scopeOverride,
    setScopeOverride,
    indexHint,
    pickerOpen,
    setPickerOpen,
    ragContextEnabled,
    navigationContextEnabled,
    hasExportableMessages,
    // Refs
    inputRef,
    listRef,
    actionMenuRef,
    abortControllerRef,
    // Send state
    lastSendPayload,
    // Thread
    threadId,
    threadsOpen,
    setThreadsOpen,
    threads,
    threadsLoading,
    activeThreadDisplayTitle,
    loadThreads,
    handleSelectThread,
    handleDeleteThread,
    // Handlers
    loadAgents,
    loadDefaults,
    handleModeChange,
    handleAgentChange,
    handleAgentSelect,
    pickScope,
    send,
    retryLast,
    handleStop,
    handleFeedback,
    handleCopyConversation,
    handleExportMarkdown,
    handleExportPdf,
    handleCopyResponse,
    clearChat,
    handleKeyDown,
    sendOrQueue,
    sendOrQueueWithFormat,
    queuedQuestion,
    slashPickerOpen: slashCommands.pickerOpen,
    slashQuery: slashCommands.slashQuery,
    slashSelectedIndex: slashCommands.selectedIndex,
    onSlashSelect: slashCommands.executeCommand,
    formatModifier: slashCommands.formatModifier,
    onClearFormatModifier: slashCommands.clearFormatModifier,
    handleMessageAnswer,
    handleMessageScopeSelect,
    handleMessageInlineScopeSelect,
    handleMessageNavigate,
    handleSetInputFromAction,
    handleFollowUp,
    handleRetryFailed,
  };
}
